import React from 'react';
import c from 'classnames';
import type { HTMLMotionProps } from 'framer-motion';
import { motion } from 'framer-motion';
import { CSSTransition } from 'react-transition-group';
import { styled, css } from 'styled-components';

import { Media } from '../../theme/Media.tsx';
import { useTableContext } from './context.tsx';
import TbodySeparator from './TbodySeparator.tsx';
import Td, { Wrapper as TdWrapper } from './Td.tsx';
import Tr, { Wrapper as TrWrapper } from './Tr.tsx';

export const Wrapper = styled.tbody<{ $isOpen?: boolean; $inGroup?: boolean; $isCollapsedNav?: boolean }>`
  outline: ${({ $isOpen, theme }) => $isOpen && `2pt solid ${theme.colors.main}`};
  outline-offset: 2px;
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    display: flex;
    flex-direction: column;
    margin-bottom: ${({ $isOpen }) => ($isOpen ? '10px' : '4px')};
    width: 100%;
    background: ${({ theme }) => theme.colors.grey00_1};
    ${({ $isOpen, $inGroup, theme }) =>
      !$isOpen &&
      css`
        border-radius: ${$inGroup ? '0px' : '4px'};
        border: 1px solid ${theme.colors.grey10_1};
        ${theme.shadows.shadowTuna};
      `}
    > ${TrWrapper} {
      margin-bottom: 0;
      > ${TdWrapper} {
        background: transparent;
      }
    }
  }
  @media ${Media.mdMin} {
    @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
      > ${TrWrapper} {
        width: 100%;
        &:nth-child(even) {
          margin-left: 0;
        }
      }
    }
  }
`;

const MotionDiv = styled(motion.div ?? 'div')<HTMLMotionProps<'div'>>``;

const subContentAnimate = {
  open: {
    height: 'auto',
    transition: {
      type: 'spring',
      damping: 10,
      mass: 0.5,
    },
    transitionEnd: {
      overflow: 'visible',
    },
  },
  closed: {
    height: 0,
    overflow: 'hidden',
    transition: {
      damping: 10,
      mass: 0.5,
    },
  },
};

export interface ITbody extends React.PropsWithChildren {
  isOpen?: boolean;
  subContent?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  dataTestId?: string;
  className?: string;
  inGroup?: boolean;
}

const Tbody: React.FC<ITbody> = ({ isOpen, subContent, children, dataTestId, onClick, className, inGroup = false }) => {
  const { isCollapsedNav } = useTableContext();
  return (
    <>
      {!!(isOpen && subContent) && <TbodySeparator />}
      <Wrapper
        className={c(className, inGroup && '_in-group', isOpen && '_is-open')}
        $isOpen={!!(subContent && isOpen)}
        $inGroup={inGroup}
        onClick={onClick}
        data-testid={dataTestId}
        $isCollapsedNav={isCollapsedNav}>
        {children}
        {subContent && (
          <CSSTransition in={isOpen} timeout={300} unmountOnExit>
            <Tr>
              <Td type='sub'>
                <MotionDiv animate={isOpen ? 'open' : 'closed'} variants={subContentAnimate} initial='closed'>
                  {subContent}
                </MotionDiv>
              </Td>
            </Tr>
          </CSSTransition>
        )}
      </Wrapper>
      {!!(isOpen && subContent) && <TbodySeparator />}
    </>
  );
};

export default React.memo(Tbody);
